import { useTranslation } from 'react-i18next';
import { ProjectTableRow } from './ProjectTable.js';
import type { MemberRole } from './MemberRoleBadges.js';
import { HEALTH_COLORS } from '../health/HealthBadge.js';
import { calcAvgHealth } from '../health/memberHealth.js';
import type { Project } from '../../../types/project.js';

/** チームビュー 1 行分: 案件 + その行のメンバーが持つ担当ロール。 */
export interface TeamViewProjectRow {
  project: Project;
  role: MemberRole;
}

/** チームビュー 1 メンバー分: 表示名 + その人の担当案件行。 */
export type TeamViewMember = [string, TeamViewProjectRow[]];

/**
 * チームビュー: メンバー別案件一覧
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business チームメンバーごとのプロジェクト一覧をメンバー別にグルーピングし、各行にそのメンバーの担当ロール（開発者/レビュアー/開発リーダー）を添えて表示
 * @graph-connects ProjectTableRow [calls] プロジェクト行コンポーネント描画
 */
export function TeamView({
  teamView,
  onProjectClick,
  onHealthInfoClick,
}: {
  teamView: TeamViewMember[];
  onProjectClick: (p: Project) => void;
  onHealthInfoClick: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-8">
      {teamView.map(([leader, rows]) => {
        const projects = rows.map((r) => r.project);
        const avgHealth = calcAvgHealth(projects);
        return (
          <div key={leader}>
            <h4 className="text-sm font-semibold text-[var(--color-text-primary)] mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[var(--color-accent-primary)]" />
              {leader}
              <span className="text-[var(--color-text-muted)] font-normal">
                ({String(projects.length)})
              </span>
              {avgHealth && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold"
                  style={{
                    backgroundColor: HEALTH_COLORS[avgHealth.grade].bg,
                    color: HEALTH_COLORS[avgHealth.grade].text,
                  }}
                  title={`${t('projects.health.avgLabel')}${t('projects.health.title')}: ${t(
                    HEALTH_COLORS[avgHealth.grade].labelKey,
                  )} (${String(avgHealth.score)}${t('projects.health.unit')})`}
                >
                  {avgHealth.grade}
                  <span className="font-normal">
                    {t(HEALTH_COLORS[avgHealth.grade].labelKey)} {String(avgHealth.score)}
                    {t('projects.health.unit')}
                  </span>
                </span>
              )}
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col className="w-[140px]" />
                  <col className="w-[300px]" />
                  <col className="w-[130px]" />
                  <col className="w-[110px]" />
                  <col className="w-[100px]" />
                  <col className="w-[200px]" />
                  <col className="w-[80px]" />
                  <col className="w-[100px]" />
                  <col className="w-[100px]" />
                  <col className="w-[100px]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                      {t('projects.fields.issueKey')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                      {t('projects.fields.summary')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                      {t('members.detail.role')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                      {t('projects.fields.responsibleTeam')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                      {t('projects.fields.pl')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                      {t('projects.fields.completionRatio')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                      {t('projects.fields.points')}
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        {t('projects.health.title')}
                        <button
                          type="button"
                          onClick={onHealthInfoClick}
                          className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-[var(--color-text-muted)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-text-primary)] transition-colors text-[10px] leading-none"
                          aria-label={t('projects.health.title')}
                        >
                          ?
                        </button>
                      </span>
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                      {t('projects.fields.devLeader')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                      {t('projects.fields.status')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ project, role }) => (
                    <ProjectTableRow
                      key={project.id}
                      project={project}
                      role={role}
                      onProjectClick={onProjectClick}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      {teamView.length === 0 && (
        <p className="text-center py-8 text-[var(--color-text-muted)]">
          {t('projects.noProjects')}
        </p>
      )}
    </div>
  );
}
